import React, { useState, useEffect } from "react";
import { ref, update } from "firebase/database";
import { db } from "../firebase";
import useFile from "../hooks/useFile";
import Popup from "./Popup";

const EditPlayer = ({ gameId, playerId, selectedPlayer, onClose, basePrice, teams = [] }) => {
  const [player, setPlayer] = useState({
    name: "",
    role: "",
    price: basePrice || 0,
    status: "upcoming",
    image: "",
    playerNumber: "",
    mobileNo: "",
    fileId: null,
    teamId: "",
  });
  const { uploadImage, uploading, clearError, error: uploadError, deleteFile } = useFile();
  const [preview, setPreview] = useState(null);
  const [popup, setPopup] = useState({ isOpen: false, message: '', type: 'info' });
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load selected player data
  useEffect(() => {
    if (selectedPlayer) {
      setPlayer({
        name: selectedPlayer.name || "",
        role: selectedPlayer.role || "",
        price: selectedPlayer.price || basePrice || 0,
        status: selectedPlayer.status || "upcoming",
        image: selectedPlayer.image || "",
        playerNumber: selectedPlayer.playerNumber || "",
        mobileNo: selectedPlayer.mobileNo || "",
        fileId: selectedPlayer.fileId || null,
        teamId: selectedPlayer.teamId || "",
      });
      if (selectedPlayer.image) {
        setPreview(selectedPlayer.image);
      }
    }
  }, [selectedPlayer, basePrice]);

  const handleChange = (f, v) => {
    setPlayer({ ...player, [f]: v });
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    
    if (!selectedFile) {
      setPreview(null);
      setPlayer({ ...player, image: "", fileId: null });
      return;
    }

    // If there's an existing uploaded file, delete it first
    if (player.fileId) {
      await deleteFile(player.fileId);
    }

    // Create preview first
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(selectedFile);

    // Upload immediately when file is selected
    clearError();
    const result = await uploadImage(selectedFile, {
      maxSize: 500 * 1024,
      allowedTypes: ["image/jpeg", "image/png", "image/webp"],
    });

    if (result.success) {
      // Store the uploaded file info in player state
      setPlayer({
        ...player,
        image: result.fileUrl,
        fileId: result.fileId,
      });
    } else {
      // Show error and clear preview
      setPopup({
        isOpen: true,
        message: result.error || "Failed to upload image",
        type: "error",
      });
      setPreview(null);
      e.target.value = "";
    }
  };

  const handleUpdatePlayer = async (e) => {
    e.preventDefault();

    if (!gameId || !playerId) {
      setPopup({ isOpen: true, message: 'Game ID or Player ID missing', type: 'error' });
      return;
    }

    // Check if image is still uploading
    if (uploading) {
      setPopup({
        isOpen: true,
        message: "Please wait for image upload to complete",
        type: "info",
      });
      return;
    }

    setLoading(true);
    try {
      const playerRef = ref(db, `games/${gameId}/players/${playerId}`);

      await update(playerRef, {
        name: player.name,
        role: player.role,
        price: player.price,
        status: player.status,
        image: player.image,
        playerNumber: player.playerNumber,
        mobileNo: player.mobileNo,
        fileId: player.fileId || null,
        teamId: player.teamId || null,
      });

      setPopup({ 
        isOpen: true, 
        message: 'Player updated successfully!', 
        type: 'success' 
      });

      // Close edit mode after a delay
      setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, 1500);
    } catch (error) {
      console.error("Error updating player:", error);
      setPopup({ 
        isOpen: true, 
        message: 'Failed to update player: ' + error.message, 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden" }}>
      <div className="card-header bg-gradient text-white py-3" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
        <div className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0 fw-bold">
            <i className="bi bi-pencil-square me-2"></i>
            Edit Player
          </h4>
          {onClose && (
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={onClose}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </div>
      </div>

      <div className="card-body p-4">
        <form onSubmit={handleUpdatePlayer}>
          {/* Player Photo Upload */}
          <div className="mb-4 text-center">
            <div className="player-photo-upload-small mb-3">
              {preview ? (
                <div className="photo-preview-small">
                  <img
                    src={preview}
                    alt="Player preview"
                    className="rounded-circle"
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-danger remove-photo-small"
                    onClick={async () => {
                      if (player.fileId) {
                        try {
                          setDeleting(true);
                          await deleteFile(player.fileId);
                        } catch (e) {
                        } finally {
                          setDeleting(false);
                        }
                      }
                      setPreview(null);
                      setPlayer({ ...player, image: "", fileId: null });
                      const input = document.getElementById("photoUploadEdit");
                      if (input) input.value = "";
                    }}
                    disabled={uploading || loading || deleting}
                  >
                    <i className="bi bi-x-circle"></i>
                  </button>
                </div>
              ) : (
                <div className="photo-placeholder-small">
                  {uploading || deleting ? (
                    <>
                      <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
                        <span className="visually-hidden">Uploading...</span>
                      </div>
                      <p className="mb-0 small">Uploading...</p>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-camera-fill"></i>
                      <p className="mb-0 small">Photo</p>
                    </>
                  )}
                </div>
              )}
            </div>
            <input
              type="file"
              id="photoUploadEdit"
              accept="image/*"
              onChange={handleFileChange}
              disabled={uploading || loading}
              className="d-none"
            />
            <label
              htmlFor="photoUploadEdit"
              className={`btn btn-sm ${uploading || deleting ? "btn-secondary" : "btn-outline-primary"}`}
            >
              {uploading || deleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="bi bi-upload me-2"></i>
                  {preview ? "Change Photo" : "Upload Photo"}
                </>
              )}
            </label>
            <small className="d-block text-muted mt-1">Optional - Max 500KB</small>
            {uploadError && (
              <small className="d-block text-danger mt-1">
                <i className="bi bi-exclamation-circle me-1"></i>
                Please try again after some time.
              </small>
            )}
          </div>

          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-person-fill me-2 text-primary"></i>
                Player Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter player name"
                required
                value={player.name}
                onChange={(e) => handleChange("name", e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-hash me-2 text-primary"></i>
                Jersey Number
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., 10, 7"
                value={player.playerNumber}
                onChange={(e) => handleChange("playerNumber", e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-telephone-fill me-2 text-primary"></i>
                Mobile Number
              </label>
              <input
                type="tel"
                className="form-control"
                placeholder="e.g., +1234567890"
                value={player.mobileNo}
                onChange={(e) => handleChange("mobileNo", e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-award-fill me-2 text-primary"></i>
                Role <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={player.role}
                required
                onChange={(e) => handleChange("role", e.target.value)}
                disabled={loading}
              >
                <option value="">Select Your Role</option>
                <option value="Batsman">Batsman</option>
                <option value="Bowler">Bowler</option>
                <option value="All-rounder">All-rounder</option>
                <option value="Keeper">Wicket Keeper</option>
                <option value="Batsman-Keeper">Batsman + Wicket Keeper</option>
                <option value="Bowler-Keeper">Bowler + Wicket Keeper</option>
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-cash-coin me-2 text-primary"></i>
                Base Price
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="Enter base price"
                value={player.price}
                onChange={(e) => handleChange("price", e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-flag-fill me-2 text-primary"></i>
                Status
              </label>
              <select
                className="form-select"
                value={player.status}
                onChange={(e) => handleChange("status", e.target.value)}
                disabled={loading}
              >
                <option value="upcoming">Upcoming</option>
                <option value="sold">Sold</option>
                <option value="unsold">Unsold</option>
              </select>
            </div>

            <div className="col-12">
              <label className="form-label fw-semibold">
                <i className="bi bi-shield-fill me-2 text-primary"></i>
                Team Assignment
              </label>
              <select
                className="form-select"
                value={player.teamId}
                onChange={(e) => handleChange("teamId", e.target.value)}
                disabled={loading}
              >
                <option value="">Not Assigned</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.teamName}
                  </option>
                ))}
              </select>
              {teams.length === 0 && (
                <small className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  No teams available. Create teams first.
                </small>
              )}
            </div>

            <div className="col-12 mt-4">
              <button 
                type="submit" 
                className="btn btn-primary w-100 py-2 fw-semibold" 
                disabled={loading || uploading || deleting}
                style={{ fontSize: "1.05rem" }}
              >
                {loading || uploading || deleting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    {uploading ? "Uploading..." : "Updating..."}
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle-fill me-2"></i>
                    Update Player
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <Popup
        isOpen={popup.isOpen}
        onClose={() => setPopup({ isOpen: false, message: '', type: 'info' })}
        message={popup.message}
        type={popup.type}
      />

      <style jsx>{`
        .player-photo-upload-small {
          position: relative;
          width: 100px;
          height: 100px;
          margin: 0 auto;
        }

        .photo-placeholder-small {
          width: 100px;
          height: 100px;
          border: 2px dashed #dee2e6;
          border-radius: 50%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .photo-placeholder-small:hover {
          border-color: #667eea;
          background: #f0f2ff;
        }

        .photo-placeholder-small i {
          font-size: 2rem;
          color: #6c757d;
          margin-bottom: 5px;
        }

        .photo-preview-small {
          position: relative;
          width: 100px;
          height: 100px;
        }

        .photo-preview-small img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border: 3px solid #667eea;
          box-shadow: 0 3px 10px rgba(102, 126, 234, 0.3);
        }

        .remove-photo-small {
          position: absolute;
          top: -8px;
          right: -8px;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
        }

        .form-control:focus,
        .form-select:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
        }

        .bg-gradient {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transition: all 0.2s ease;
        }
      `}</style>
    </div>
  );
};

export default EditPlayer;
